import type { ApiClient, FieldSubject } from '@carbonwebmail/api-client'
import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { needsChoices } from '@carbonwebmail/domain'
import { useState } from 'react'
import { FIELD_KINDS } from './fieldKinds.js'
import { Popover } from './Popover.js'
import { useDeclareField } from './useDeclareField.js'

/**
 * The `+` past the last column heading.
 *
 * A column on this board is a field the organisation declared, so adding one is
 * declaring one — through the same view model wherever it is asked for, so the
 * key rule and every refusal read the same. What differs is where you are
 * standing when you do it: beside the table, looking at the gap the column will
 * fill.
 *
 * **It is reachable on an empty board because the table is drawn even with no
 * rows.** That was not true until 2026-09-18: the table was swapped for a card,
 * this cell went with it, and a new organisation could never declare its first
 * column. The fix was the table, not this — moving the control into the toolbar
 * instead would have answered "where do I add a column" in a different place on
 * a board than the one the owner asked for.
 *
 * It stays open after adding, because adding one column is rarely the whole
 * job, and the board behind it has already grown by the time the second is
 * typed.
 */

export function AddColumn({
  client,
  subject,
  onAdded,
}: {
  readonly client: ApiClient
  /** Which board's columns these are. Decides only where the row lands. */
  readonly subject: FieldSubject
  /** Reloads the board's fields, so the new column appears. */
  readonly onAdded: () => Promise<void> | void
}) {
  const [open, setOpen] = useState(false)
  const { draft, problem, saving, change, declare } = useDeclareField({
    client,
    subject,
    onDeclared: onAdded,
  })

  return (
    <Popover
      label="Add a column"
      onClose={() => setOpen(false)}
      onToggle={() => setOpen((was) => !was)}
      open={open}
      align="right"
      panelClassName="w-72 p-3 text-left"
      trigger="+"
      triggerClassName="h-6 w-6 rounded-field border border-line text-[14px] text-t2 leading-none hover:border-brand hover:text-brand"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void declare()
        }}
      >
        <p className="mb-2 font-semibold text-[12px] text-t2 uppercase tracking-[0.06em]">
          New column
        </p>

        {problem === undefined ? null : (
          <p className="mb-2 rounded-field border border-danger bg-danger-soft px-2 py-1.5 text-[12px] text-danger">
            {problem}
          </p>
        )}

        <label className="mb-2 block" htmlFor="new-column-label">
          <span className="mb-1 block font-medium text-[12px] text-t2">Name</span>
          <input
            autoComplete="off"
            className="w-full rounded-field border border-line bg-surface px-2 py-1.5 text-[13px] text-t1"
            disabled={saving}
            id="new-column-label"
            onChange={(event) => change('label', event.target.value)}
            type="text"
            value={draft.label}
          />
        </label>

        <label className="mb-2 block" htmlFor="new-column-type">
          <span className="mb-1 block font-medium text-[12px] text-t2">Kind</span>
          <select
            className="w-full rounded-field border border-line bg-surface px-2 py-1.5 text-[13px] text-t1"
            disabled={saving}
            id="new-column-type"
            onChange={(event) => change('type', event.target.value as ContactFieldDto['type'])}
            value={draft.type}
          >
            {FIELD_KINDS.map(([kind, label]) => (
              <option key={kind} value={kind}>
                {label}
              </option>
            ))}
          </select>
        </label>

        {needsChoices(draft.type) ? (
          <label className="mb-2 block" htmlFor="new-column-choices">
            <span className="mb-1 block font-medium text-[12px] text-t2">
              What it can be set to
            </span>
            <textarea
              className="w-full rounded-field border border-line bg-surface px-2 py-1.5 text-[13px] text-t1"
              disabled={saving}
              id="new-column-choices"
              onChange={(event) => change('choices', event.target.value)}
              rows={3}
              value={draft.choices}
            />
            <span className="mt-1 block text-[11px] text-t3">
              One per line, or leave it empty — importing adds the values it finds.
            </span>
          </label>
        ) : null}

        <button
          className="w-full rounded-pill bg-brand px-3 py-2 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
          disabled={saving || draft.label.trim() === ''}
          type="submit"
        >
          {saving ? 'Adding…' : 'Add column'}
        </button>
      </form>
    </Popover>
  )
}
